import * as i18n from "util/i18n";
import { getMultipleTransfersI18nKey } from "util/multiple-transfers";
import { getTransferActivityName } from "util/transferInternalTitleLabels";

/**
 * Importes ARS y USD de una fila de transacción seleccionada (misma lógica que SelectedTransactionsAmount).
 * @param {object} row
 * @returns {{ ars: number, usd: number }}
 */
export const getRowArsUsd = (row) => {
    const ta = row?.transactionAmounts || {};
    const ars = ta["0"] ?? ta["01"] ?? ta["032"] ?? 0;
    const usd = ta["2"] ?? ta["02"] ?? ta["840"] ?? 0;
    return {
        ars: Number(ars) || 0,
        usd: Number(usd) || 0,
    };
};

export const getActivityLabel = (row) => {
    const idActivity = row?.transaction?.idActivity;
    const activityName = row?.transaction?.activityName;
    if (idActivity === "transfer.multiple.send") {
        return i18n.get(getMultipleTransfersI18nKey("activities.transfer.multiple.send"));
    }
    return getTransferActivityName(idActivity, activityName) || idActivity || "";
};

/** Orden de moneda dentro del mismo tipo: ARS, USD, sin monto. */
const CURRENCY_SORT = { "0": 0, "2": 1, none: 2 };

/**
 * Agrupa por tipo (idActivity) y moneda: una línea por par (actividad, moneda) con conteo y suma solo en esa moneda.
 * Orden: idActivity, luego moneda (ARS, USD; filas solo con montos en cero al final por actividad).
 * @param {object[]} rows
 * @returns {{ idActivity: string, currency: string, count: number, label: string, amount: number }[]}
 */
export const buildGroupedSummary = (rows) => {
    if (!rows?.length) {
        return [];
    }
    const byKey = new Map();

    const ensure = (idActivity, currency, labelFn) => {
        const key = `${idActivity}\0${currency}`;
        if (!byKey.has(key)) {
            byKey.set(key, {
                idActivity,
                currency,
                count: 0,
                label: labelFn(),
                amount: 0,
            });
        }
        return byKey.get(key);
    };

    rows.forEach((row) => {
        const idActivity = row?.transaction?.idActivity ?? "";
        const { ars, usd } = getRowArsUsd(row);
        const label = () => getActivityLabel(row);

        if (ars <= 0 && usd <= 0) {
            const g = ensure(idActivity, "none", label);
            g.count += 1;
            return;
        }
        if (ars > 0) {
            const g = ensure(idActivity, "0", label);
            g.count += 1;
            g.amount += ars;
        }
        if (usd > 0) {
            const g = ensure(idActivity, "2", label);
            g.count += 1;
            g.amount += usd;
        }
    });

    const list = Array.from(byKey.values());
    list.sort((a, b) => {
        const cmp = String(a.idActivity).localeCompare(String(b.idActivity));
        if (cmp !== 0) {
            return cmp;
        }
        return CURRENCY_SORT[a.currency] - CURRENCY_SORT[b.currency];
    });

    return list;
};
